import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { getImageUrl, getBlurUrl, getSizedUrl } from "../utils/imageUtils";
import SmartImage from "./SmartImage";

const AUTOPLAY_MS = 4000;

// Resolves a slide's display data from the live product (auto-syncs price/image/url)
const resolveSlide = (slide, productMap) => {
  const product = productMap[slide.productId];
  if (!product) return null;
  const banner = slide.customBanner || product.images?.[0];
  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    price: product.price,
    originalPrice: product.originalPrice,
    image: banner,
    tagline: slide.tagline || "",
    discount:
      product.originalPrice && product.price && product.originalPrice > product.price
        ? Math.round((1 - product.price / product.originalPrice) * 100)
        : null,
  };
};

const isScheduledNow = (slide) => {
  const now = new Date();
  if (slide.startDate && new Date(slide.startDate) > now) return false;
  if (slide.endDate && new Date(slide.endDate) < now) return false;
  return true;
};

const FeaturedSlider = ({ products = [], slides = [] }) => {
  const navigate = useNavigate();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const pausedRef = useRef(false);

  const productMap = {};
  products.forEach((p) => { productMap[p.id] = p; });

  const productToItem = (p) => ({
    id: p.id,
    slug: p.slug,
    name: p.name,
    price: p.price,
    originalPrice: p.originalPrice,
    image: p.images?.[0],
    tagline: "",
    discount:
      p.originalPrice && p.price && p.originalPrice > p.price
        ? Math.round((1 - p.price / p.originalPrice) * 100)
        : null,
  });

  let items = (slides || [])
    .filter((s) => s.enabled !== false && isScheduledNow(s))
    .sort((a, b) => (b.priority ?? -1) - (a.priority ?? -1))
    .map((s) => resolveSlide(s, productMap))
    .filter(Boolean);

  // Fallback: when no slides configured, auto-fill with the latest products
  if (items.length === 0 && products.length > 0) {
    items = [...products]
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
      .slice(0, 6)
      .map(productToItem);
  }

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  // Auto-play every 4s, pause on hover/touch
  useEffect(() => {
    if (!emblaApi || items.length <= 1) return;
    const id = setInterval(() => {
      if (!pausedRef.current) emblaApi.scrollNext();
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, items.length]);

  if (items.length === 0) return null;

  return (
    <section className="bg-[#F9F8F6]" data-testid="featured-slider">
      <div
        className="overflow-hidden"
        ref={emblaRef}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
        onPointerDown={() => { pausedRef.current = true; }}
        onPointerUp={() => { setTimeout(() => { pausedRef.current = false; }, 800); }}
      >
        <div className="flex">
          {items.map((item, idx) => {
            const src = (item.image || "").startsWith("/api/") ? getImageUrl(item.image) : item.image;
            return (
              <div
                key={`${item.id}-${idx}`}
                className="relative flex-[0_0_100%] min-w-0 cursor-pointer group"
                onClick={() => navigate(`/product/${item.slug || item.id}`)}
                data-testid={`featured-slide-${idx}`}
              >
                <div className="container mx-auto px-6 md:px-12 py-10 md:py-16">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center">
                    {/* Image */}
                    <div className="relative order-1 overflow-hidden bg-[#F2EFEB] aspect-[4/5] md:aspect-[3/4] max-w-md w-full mx-auto">
                      <SmartImage
                        src={(item.image || "").startsWith("/api/") ? getSizedUrl(item.image, 800) : src}
                        blurSrc={getBlurUrl(item.image)}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        eager={idx === 0}
                      />
                      {item.discount && (
                        <span className="absolute top-4 left-4 bg-[#7A2E35] text-white text-xs font-medium tracking-[0.12em] uppercase px-3 py-1.5" data-testid={`featured-discount-${idx}`}>
                          {item.discount}% Off
                        </span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="order-2 text-center md:text-left">
                      <span className="overline block mb-4">Featured Blouse</span>
                      <h2 className="font-heading text-3xl md:text-5xl font-light text-[#1A1A1A] leading-tight tracking-tight mb-4">
                        {item.name}
                      </h2>
                      {item.tagline && (
                        <p className="text-base md:text-lg text-[#68645E] font-light mb-5 max-w-md mx-auto md:mx-0">{item.tagline}</p>
                      )}
                      <div className="flex items-baseline gap-3 justify-center md:justify-start mb-7">
                        {item.price != null && (
                          <span className="font-heading text-2xl md:text-3xl text-[#1A1A1A]">₹{item.price.toLocaleString()}</span>
                        )}
                        {item.originalPrice && item.originalPrice > (item.price || 0) && (
                          <span className="text-base text-[#A39E96] line-through">₹{item.originalPrice.toLocaleString()}</span>
                        )}
                      </div>
                      <span className="btn-primary pointer-events-none inline-flex" data-testid={`featured-shop-now-${idx}`}>
                        Shop Now
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination dots */}
      {items.length > 1 && (
        <div className="flex justify-center gap-2 pb-8 -mt-2">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`featured-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-300 ${selected === i ? "w-6 bg-[#7A2E35]" : "w-2 bg-[#D6CFC4] hover:bg-[#B8B2A8]"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default FeaturedSlider;
